import type { RootState } from "@/store";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Lifecycle of the relay-origin cookie.
 *
 * A tri-state rather than a `ready` boolean because "not ready" covers two
 * situations a user must be able to tell apart: a handshake still in flight,
 * and one that failed. While the handshake ran exactly once the difference did
 * not exist, so the Capture page showed "Searching for camera..." forever after
 * a permanent failure.
 */
export type RelayStatus = "connecting" | "ready" | "error";

export interface RelayState {
  /** Relay origin, learned from the control plane. Null = no relay configured. */
  relayUrl: string | null;
  status: RelayStatus;
  error: string | null;
  /**
   * Bumped to ask {@link useRelaySession} to restart the handshake now.
   *
   * In the store rather than in the hook so the retry button can live wherever
   * the failure is rendered — several pages deep — without threading a callback
   * down from `Authenticated`, which is the only place the hook is mounted.
   */
  reconnectNonce: number;
}

const initialState: RelayState = {
  relayUrl: null,
  // The handshake starts as soon as an organization is known, so anything that
  // renders before it resolves is genuinely connecting, not idle.
  status: "connecting",
  error: null,
  reconnectNonce: 0,
};

/**
 * The relay origin arrives verbatim from `RELAY_URL` and is concatenated with
 * `/d/{deviceId}`, so a trailing slash would produce `https://relay//d/abc`.
 * Normalizing once here keeps every consumer from having to.
 */
const normalizeOrigin = (url: string): string => url.replace(/\/+$/, "");

export const relaySlice = createSlice({
  name: "relay",
  initialState,
  reducers: {
    /**
     * A handshake attempt the user should see as "connecting": first mount, an
     * organization switch, or a manual retry.
     *
     * An organization switch must land here *before* the re-mint is awaited.
     * The cookie is scoped to one organization, so leaving the status at
     * "ready" across the switch authorizes camera requests against the previous
     * fleet. Automatic backoff retries deliberately do not dispatch this — they
     * leave the error on screen rather than flickering it away and back.
     */
    relaySessionStarted: (state) => {
      state.status = "connecting";
      state.error = null;
    },
    relaySessionEstablished: (state, { payload }: PayloadAction<string>) => {
      state.relayUrl = normalizeOrigin(payload);
      state.status = "ready";
      state.error = null;
    },
    relaySessionFailed: (state, { payload }: PayloadAction<string>) => {
      state.status = "error";
      state.error = payload;
    },
    relayReconnectRequested: (state) => {
      state.reconnectNonce += 1;
    },
  },
});

export const {
  relaySessionStarted,
  relaySessionEstablished,
  relaySessionFailed,
  relayReconnectRequested,
} = relaySlice.actions;

export const selectRelayUrl = (state: RootState): string | null =>
  state.relay.relayUrl;

export const selectRelayStatus = (state: RootState): RelayStatus =>
  state.relay.status;

export const selectRelaySessionReady = (state: RootState): boolean =>
  state.relay.status === "ready";

export const selectRelayReconnectNonce = (state: RootState): number =>
  state.relay.reconnectNonce;
